/**
 * grading-api.ts — the ops Grading page's calls to the community service's staff routes (ADR-133 Amendment 2 §1;
 * COMMUNITY build 3 Stage B, `s9-grading-ops-page`). S9 · Community.
 *
 * ⭐ ZONE-SCOPED. Grading runs inside each AWS zone, in the zone's own tables (Amendment 2 §1 "Zones"), so every list
 * read is made against ONE zone's admin API and the page fans the lists out across the configured zones, tagging each
 * row with the zone it came from (ADR-122 Pattern 1). A write goes to the zone the row came from — a site, a grade and
 * a grader's attachment live in exactly one zone. Rules and the Workflow figures' words are one set for the pool: the
 * page writes them to every configured zone and says which, if any, did not take the change.
 *
 * The routes and shapes are the contract the community service answers (`lambdas/community/src/admin.ts`); the
 * service decides who may do what (the staff Cedar actions `grading:read` · `grading:configure` · `grading:moderate`
 * · `grading:queue`) and tells the page through `GET /admin/grading/me`, so the page never infers a permission from a
 * role name.
 */
import { adminApiInZone } from './api-client';
import { formatMonth } from './time-format';
import type { PortalZone } from './zones';

export type Pool = 'community' | 'invited' | 'professional';
export type GraderStatus = 'active' | 'suspended' | 'removed';
export type Standing = 'in_step' | 'drifting' | 'out_of_step' | 'flagged' | 'new';
export type SiteState = 'Queued' | 'Offered' | 'Grading' | 'Closed' | 'Released' | 'Carried';

export interface GradingCan { configure: boolean; moderate: boolean; queue: boolean }
export interface GradingMe { zone: string; can: GradingCan }

export interface GraderRow {
  sub: string;
  graderNumber: string;
  /** The grader's name from their sign-in record; null when it gave none (shown as the number). Absent from an older server. */
  name?: string | null;
  /** The newest grading sitting they started; null before any. Absent from an older server. */
  lastSittingAt?: string | null;
  email: string | null;
  pool: Pool;
  status: GraderStatus;
  grantedAt: string | null;
  sitesGraded: number;
  quarantined: number;
  standing: Standing;
  ageBand: string | null;
  gender: string | null;
  region: string | null;
}

export interface SiteRow {
  siteId: string;
  state: SiteState;
  host: string;
  accountId: string;
  domainToken: string;
  pages: number;
  kinds: string[];
  offered: number;
  accepted: number;
  queuedAt: string;
  closeAt: string;
  headStart: { groups: string[]; until: string } | null;
  withdrawnAt: string | null;
  releasedAt: string | null;
  result: { band: string; graders: number; stayShare: number } | null;
  origin: 'monthly' | 'manual';
  queuedBy: string | null;
  carriedFrom: string | null;
  offerOnly: boolean;
}

export interface BatchesResponse {
  zone: string;
  month: string;
  months: string[];
  batchDay: number;
  closeAfterDays: number;
  totals: Record<SiteState, number>;
  sites: SiteRow[];
}

export interface SiteDetail {
  site: SiteRow;
  offers: { graderNumber: string; state: string; offeredAt: string; headStart: boolean; takenAt: string | null }[];
  grades: {
    graderNumber: string; pool: Pool; state: string; bands: (number | null)[]; latenciesMs: (number | null)[];
    tells: string[]; quarantined: boolean; quarantine: { at: string; by: string; reason: string } | null;
    weight: number; submittedAt: string | null; like: string; dislike: string;
  }[];
}

export interface QualityItem {
  siteId: string;
  month: string;
  sub: string;
  graderNumber: string;
  pool: Pool;
  tells: string[];
  quarantined: boolean;
  quarantine: { at: string; by: string; reason: string } | null;
  weight: number;
  submittedAt: string | null;
  siteState: SiteState;
}

export interface RulesResponse {
  rules: Record<string, number>;
  defaults: Record<string, number>;
  bounds: Record<string, [number, number]>;
  help: Record<string, string>;
  labels: Record<string, string>;
  updatedAt: string | null;
  updatedBy: string | null;
}

export type FigureId = '1a' | '1b' | '2' | '3' | '4' | '5';
export interface Figure {
  figure: FigureId;
  src: string;
  title: string;
  purpose: string;
  process: string;
  edited: { at: string; by: string } | null;
}

export interface PoolResponse {
  zone: string;
  month: string;
  graders: {
    total: number;
    byPool: Record<Pool, number>;
    byAge: { u40: number; o40: number; unknown: number };
    byGender: { woman: number; man: number; other: number };
    byRegion: Record<string, number>;
  };
  sites: { total: number; live: number; released: number; carried: number };
  gradesThisMonth: number;
  shortGroups: string[];
}

export interface CandidateSite { accountId: string; domainToken: string; host: string; alreadyThisMonth: boolean }

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) });
const enc = encodeURIComponent;

export const getGradingMe = (z: PortalZone) => adminApiInZone<GradingMe>(z, '/admin/grading/me');
export const listGraders = (z: PortalZone) => adminApiInZone<{ zone: string; graders: GraderRow[] }>(z, '/admin/grading/graders');
export const listBatches = (z: PortalZone, month?: string) =>
  adminApiInZone<BatchesResponse>(z, `/admin/grading/batches${month ? `?month=${enc(month)}` : ''}`);
export const getSite = (z: PortalZone, siteId: string) => adminApiInZone<SiteDetail>(z, `/admin/grading/sites/${enc(siteId)}`);
export const listQuality = (z: PortalZone) => adminApiInZone<{ zone: string; items: QualityItem[] }>(z, '/admin/grading/quality');
export const getRules = (z: PortalZone) => adminApiInZone<RulesResponse>(z, '/admin/grading/rules');
export const getWorkflow = (z: PortalZone) => adminApiInZone<{ figures: Figure[] }>(z, '/admin/grading/workflow');
export const getPool = (z: PortalZone, month?: string) =>
  adminApiInZone<PoolResponse>(z, `/admin/grading/pool${month ? `?month=${enc(month)}` : ''}`);
export const listCandidates = (z: PortalZone) => adminApiInZone<{ zone: string; sites: CandidateSite[] }>(z, '/admin/grading/candidates');

export const moderateGrade = (z: PortalZone, siteId: string, graderNumber: string, action: 'keep' | 'quarantine', reason: string) =>
  adminApiInZone<{ grade: QualityItem; recompute: string }>(z, `/admin/grading/quality/${enc(siteId)}/${enc(graderNumber)}`, { method: 'POST', ...json({ action, reason }) });
export const putRules = (z: PortalZone, edit: Record<string, number>) =>
  adminApiInZone<RulesResponse>(z, '/admin/grading/rules', { method: 'PUT', ...json(edit) });
export const putFigure = (z: PortalZone, figure: FigureId, v: { title: string; purpose: string; process: string }) =>
  adminApiInZone<Figure>(z, `/admin/grading/workflow/${enc(figure)}`, { method: 'PUT', ...json(v) });
export const queueBatchNow = (z: PortalZone, accountId: string, domainToken: string) =>
  adminApiInZone<{ site: SiteRow; offered: number }>(z, '/admin/grading/batches/queue', { method: 'POST', ...json({ accountId, domainToken }) });

/** The words a Workflow figure's Purpose and Process may run to (ops mock version 11 §B1). PURE. */
export const FIGURE_WORD_LIMIT = 25;
export const wordsOf = (t: string): number => (t.trim() ? t.trim().split(/\s+/).length : 0);

/** A rules edit inside its bounds, or the first key that is not. PURE — the server checks again. */
export function checkRulesEdit(edit: Record<string, number>, bounds: Record<string, [number, number]>): string | null {
  for (const [k, v] of Object.entries(edit)) {
    const b = bounds[k];
    if (!b || !Number.isFinite(v) || v < b[0] || v > b[1]) return k;
  }
  return null;
}

/** "October 2026" from "2026-10" — the console's one formatter. PURE. */
export const monthLabel = (m: string): string => formatMonth(m);

export const POOL_LABEL: Record<Pool, string> = { community: 'community', invited: 'invited · paid', professional: 'industry professional' };
export const STANDING_LABEL: Record<Standing, string> = {
  in_step: 'In step', drifting: 'Drifting', out_of_step: 'Out of step', flagged: 'Flagged', new: 'Too few sites',
};
export const TELL_LABEL: Record<string, string> = {
  timing_floor: 'Answer under the readiness floor',
  uniform_timing: 'The same timing on every page',
  honeypot: 'Hidden field filled',
  out_of_step: 'Out of step with other graders',
  flagged_agreement: 'Agreement near zero',
};

// ── s9-outcome-report (ADR-137 D8) — the Testimonials tab ─────────────────────────────────────────────────────────────────────────────────
// Every testimonial of a zone in every state, its credit and the business's "What hasn't worked?" (the team's feedback, read-only). Ops
// unpublishes or deletes — behind a confirmation, audited, gated on grading:moderate — and NEVER edits a business's words (there is no route).
export type TestimonialState = 'draft' | 'live' | 'unpublished' | 'deleted' | 'stale';
export interface TestimonialCredit { state: 'pending' | 'eligible' | 'forfeited' | 'applied'; amountUsd: number; earnedAt: string; dueAt: string; eligibleAt: string | null; forfeitedAt: string | null; forfeitReason: 'unpublished' | 'deleted' | null; appliedAt: string | null }
export interface TestimonialStory { words: string; stars: number | null; businessName: string | null; sector: string | null; country: string | null; region: string | null; date: string; figures: { revenueGrowthPct: number | null; roiPct: number; paybackMonths: number | null } | null }
export interface TestimonialRow {
  testimonialId: string; zone: string; accountId: string; domainToken: string; hostname: string; businessName: string; sector: string | null;
  country: string | null; region: string | null; state: TestimonialState; liveSince: string | null; liveAt: string | null; unpublishedAt: string | null;
  deletedAt: string | null; deleteReason: string | null; staleAt: string | null; updatedAt: string;
  story: TestimonialStory; words: string; draftWords: string | null; display: { profile: boolean; brand: boolean; projection: boolean }; hasLogo: boolean;
  credit: TestimonialCredit | null; worse: string;
}
export interface FeedbackOnlyRow { testimonialId: null; zone: string; accountId: string; domainToken: string; state: 'none'; worse: string; updatedAt: string }
export const listTestimonials = (z: PortalZone) => adminApiInZone<{ zone: string; testimonials: TestimonialRow[]; feedbackOnly: FeedbackOnlyRow[] }>(z, '/admin/grading/testimonials');
export const moderateTestimonial = (z: PortalZone, id: string, act: 'unpublish' | 'delete') =>
  adminApiInZone<{ testimonialId: string; state: TestimonialState; credit: TestimonialCredit | null }>(z, `/admin/grading/testimonials/${enc(id)}/${act}`, { method: 'POST', body: '{}' });
